import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Redirect, useHistory, useLocation, useParams } from 'react-router-dom';
import cx from 'classnames';
import qs from 'query-string';

import {
  btag,
  CasinoNewProvider,
  FreespinsApi,
  isUserActionNeedPhotoVerification,
  isUserNeedKyc,
  isUserNeedVerification,
  NotificationManager,
  PhotoVerificationAction,
  pushToDataLayer,
  SiteConfigManager,
  useApplicationState,
  useCasinoGameState,
  useSettings,
} from '@atlas/core';
import { clientProfileRoutes, COMPONENT_TYPES, paymentRoutes, staticRoutes } from '@atlas/routing';
import { Button, EmptyStateView, Icon, Label, Loader } from '@atlas/ui';

import { ACCOUNT_STATUSES } from 'common/constants';
import ModalPortal from 'common/ModalPortal/ModalPortal';
import { CDN_TYPE_GAME, imageUrlCDN } from 'common/utils/utils';
import { GROUPS } from 'components/Pages/Casino/constants';
import { useAccountBlocked } from 'hooks/useAccountBlocked';
import QuickDepositPopup from 'src/common/Popup/QuickDepositPopup/QuickDepositPopup';
import useSignInRedirects from 'src/components/Auth/useSignInRedirects';
import PaymentIframe from 'src/components/ClientProfile/Payment/Deposit/PaymentIframe';
import InsufficientBalance from 'src/components/Pages/Casino/InsufficientBalance';
import { useSelfRegulationContext } from 'src/components/SelfRegulationProvider';
import { getGameIdentifier, getLinkFromCasinoGameData } from 'src/helpers/casino';
import { useCasinoGameBroadcastChannel } from 'src/hooks/useCasinoGameBroadcastChannel';
import useFreeSpins from 'src/hooks/useFreeSpins';

const FIELD_NAME = 'spinAmount';

const {
  FreeSpinsStatus: { ACTIVATED },
} = FreespinsApi;

const CasinoGameDetails = () => {
  const iframeRef = useRef(null);
  const isInitialMount = useRef(false);
  const homePageFunctionality = SiteConfigManager.getConfig(
    'config.config.casino.mobile_home_page',
  );
  const { gameId } = useParams();
  const { search } = useLocation();
  const { lang } = useSettings();
  const history = useHistory();
  const {
    location: { pathname },
    push: historyPush,
  } = history;

  const { redirectToSignIn } = useSignInRedirects();
  const applicationState = useApplicationState();
  const { account, authenticated, wallet } = applicationState;
  const { BONUS = 0, CASH = 0, FROZEN = 0 } = wallet.detailedBalance || {};
  const balance = BONUS + CASH + FROZEN;
  const casinoPath = SiteConfigManager.getComponentPath(COMPONENT_TYPES.CASINO);
  const clientProfilePath = SiteConfigManager.getComponentPath(COMPONENT_TYPES.CLIENT_PROFILE);
  const link = `${clientProfilePath}/${clientProfileRoutes.payment}/${paymentRoutes.deposit}`;
  const parsedQuery = qs.parse(search);
  const isDemo = !!parsedQuery.demo;
  const providerName = parsedQuery.provider;
  const from = parsedQuery.from;
  const fromPath = from && !from.startsWith('/') ? `/${from}` : from;
  const prevBalanceRef = useRef(balance);
  const isInsufficientBalanceShown = useRef(false);
  const [showQuickDepositPopup, setShowQuickDepositPopup] = useState(false);
  const [showInsufficientBalancePopup, setShowInsufficientBalancePopup] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState();
  const quickDepositPopupEnabled = SiteConfigManager.getConfig(
    'config.config.general.quickDepositPopup.enabledOnGamePage',
  );
  const accountBlocked = useAccountBlocked();
  const demoButtonStateForSignedInUsers = SiteConfigManager.getConfig(
    'config.config.casino.mobile_demo_logged',
    false,
  );
  const insufficientBalanceEnabled = SiteConfigManager.getConfig(
    'config.config.general.quickDepositPopup.enabledOnGameInitialization',
  );
  const demoButtonStateForNonSignedInUsers = SiteConfigManager.getConfig(
    'config.config.casino.mobile_demo_non_logged',
    false,
  );
  const backToCasinoPath = fromPath ? fromPath.split('?')[0] : casinoPath;

  const { error, game, gameDetailsData, gameResolved } = useCasinoGameState({
    gameId,
    isDemo,
    providerName,
  });

  const { showSelfRegulationPopup, showSpentLimitPopup } = useSelfRegulationContext();

  const { freeSpins, loading } = useFreeSpins({
    statuses: [ACTIVATED],
  });

  const isGameHasFreeSpins = useMemo(
    () => freeSpins.some(spin => spin.freeSpin?.casinoLimit?.gameIds?.includes(gameId)),
    [freeSpins, gameId],
  );

  useEffect(() => {
    if (account.status === ACCOUNT_STATUSES.PARTIALLY_REGISTERED) {
      history.replace(staticRoutes.signup);
    }
  }, [account.status, history.replace]);

  useEffect(() => {
    if (game) {
      const gameIdentifier = getGameIdentifier(game);
      const { ...restGame } = game;

      pushToDataLayer(
        isDemo ? 'open_demo_game' : 'open_casino_game',
        {
          gameId: gameIdentifier,
          ...restGame,
          iconUrl: imageUrlCDN(game.iconUrl, CDN_TYPE_GAME),
          language: lang,
        },
        btag,
      );
    }
  }, [game, isDemo, lang]);

  useEffect(() => {
    if (!isInitialMount.current) {
      isInitialMount.current = true;
    } else {
      location.reload();
    }
  }, [isDemo]);

  useEffect(() => {
    if (!authenticated && !isDemo) {
      redirectToSignIn({
        casinoGameId: gameId,
        providerName,
        redirectPath: `${pathname}${search}`,
      });
    }
  }, [authenticated, gameId, isDemo, providerName, pathname, search, redirectToSignIn]);

  const handlePlayGame = useCallback(() => {
    if (balance === 0) {
      return historyPush(link);
    }

    if (!authenticated) {
      redirectToSignIn({
        casinoGameId: gameId,
        providerName,
        redirectPath: `${pathname}${search}`,
      });
      return;
    }

    const gameUrl = new URL(pathname, location.origin);

    gameUrl.searchParams.set('provider', providerName);
    gameUrl.searchParams.set('from', fromPath);

    historyPush(gameUrl.pathname + gameUrl.search);
  }, [
    authenticated,
    balance,
    fromPath,
    gameId,
    providerName,
    historyPush,
    link,
    pathname,
    search,
    redirectToSignIn,
  ]);

  useEffect(() => {
    if (authenticated && insufficientBalanceEnabled && !isDemo) {
      if (balance === 0 && prevBalanceRef.current === 0 && !loading && !isGameHasFreeSpins) {
        isInsufficientBalanceShown.current = true;
        setShowInsufficientBalancePopup(true);
      }
    }
  }, [authenticated, balance, insufficientBalanceEnabled, isDemo, isGameHasFreeSpins, loading]);

  useEffect(() => {
    if (showInsufficientBalancePopup && prevBalanceRef.current === 0 && balance > 0) {
      const notificationId = NotificationManager.display({
        data: {
          message: 'account_topped_up_message',
          title: 'account_topped_up_title',
        },
        type: 'accountToppedUp',
      });

      prevBalanceRef.current = balance;

      setTimeout(() => {
        isInsufficientBalanceShown.current = false;
        setShowInsufficientBalancePopup(false);
        NotificationManager.removeNotification(notificationId);
      }, 3000);
    }
  }, [balance, showInsufficientBalancePopup]);

  useEffect(() => {
    const isQuickDepositPopupNotAllowed =
      !authenticated ||
      !quickDepositPopupEnabled ||
      isDemo ||
      isInsufficientBalanceShown.current ||
      (insufficientBalanceEnabled && loading);

    if (isQuickDepositPopupNotAllowed) {
      return;
    }

    const spinAmount = window.localStorage.getItem(FIELD_NAME);

    if (spinAmount && balance === prevBalanceRef.current && Number(spinAmount) > balance) {
      setShowQuickDepositPopup(true);
      return;
    }

    if (prevBalanceRef.current > balance) {
      const currentSpinAmount = prevBalanceRef.current - balance;

      if (Number(spinAmount) !== currentSpinAmount) {
        window.localStorage.setItem(FIELD_NAME, currentSpinAmount.toString());
      }

      if (currentSpinAmount > balance) {
        setShowQuickDepositPopup(true);
      }
    }

    if (prevBalanceRef.current !== balance) {
      prevBalanceRef.current = balance;
    }
  }, [
    authenticated,
    balance,
    insufficientBalanceEnabled,
    isDemo,
    loading,
    quickDepositPopupEnabled,
  ]);

  const src = useMemo(() => getLinkFromCasinoGameData(gameDetailsData), [gameDetailsData]);

  const className = cx('casino-game-container', { 'is-demo': isDemo });

  useEffect(() => {
    const params = new URLSearchParams(search);

    const isDemoDisabled =
      (authenticated && !demoButtonStateForSignedInUsers) ||
      (!authenticated && !demoButtonStateForNonSignedInUsers);

    if (isDemo && isDemoDisabled) {
      params.delete('demo');

      historyPush(`${pathname}?${params.toString()}`);
    }
  }, [
    pathname,
    search,
    authenticated,
    demoButtonStateForSignedInUsers,
    demoButtonStateForNonSignedInUsers,
    historyPush,
    isDemo,
  ]);

  const isDemoEnabled =
    isDemo &&
    ((authenticated && demoButtonStateForSignedInUsers) ||
      (!authenticated && demoButtonStateForNonSignedInUsers));

  const onGoBack = useCallback(() => {
    historyPush(backToCasinoPath);
  }, [backToCasinoPath, historyPush]);

  const closeQuickDepositPopup = useCallback(() => {
    setShowQuickDepositPopup(false);
  }, [setShowQuickDepositPopup]);

  useCasinoGameBroadcastChannel({ backPath: backToCasinoPath, iframeRef, provider: providerName });

  if (accountBlocked) {
    return <Redirect to={{ pathname: '/casino', state: { accountBlocked: true } }} />;
  }

  if (
    authenticated &&
    isUserNeedKyc(account) &&
    isUserActionNeedPhotoVerification(account, PhotoVerificationAction.CASINO_GAME)
  ) {
    return <Redirect to="/account/verification" />;
  }

  if (
    authenticated &&
    isUserNeedVerification(applicationState.account) &&
    isUserActionNeedPhotoVerification(account, PhotoVerificationAction.CASINO_GAME)
  ) {
    return <Redirect to="/account/profile/account-numbers" />;
  }

  if (showSpentLimitPopup || showSelfRegulationPopup) {
    return (
      <Redirect
        to={{
          pathname: casinoPath,
          state: {
            redirectAfterPopup: `${pathname}${search}`,
            selfRegulationPopupShow: true,
            showRealityCheckPopup: true,
          },
        }}
      />
    );
  }

  return (
    <div className="casino-game-details">
      <ModalPortal backdropOff className="casino-game">
        <div className=" profile-wrapper-absolute">
          {!gameResolved && (
            <div className="casino-game-state loader-state">
              <Loader height={49} width={49} />
              <EmptyStateView description="game_please_wait" title="game_please_wait" />
            </div>
          )}
          {error &&
            (() => {
              const notFoundError = error.code === 404 || error.status === 404;

              return (
                <div className="casino-game-state">
                  <Icon height={49} name={notFoundError ? 'not-found' : 'icon-error'} width={49} />
                  <EmptyStateView
                    description={notFoundError ? 'game_is_unavailable' : 'game_try_again'}
                    title={notFoundError ? 'game_not_found' : 'game_failed'}
                  >
                    <Link to={backToCasinoPath}>
                      <Button
                        label={homePageFunctionality ? 'game_back_home' : 'game_back'}
                        size="medium"
                      />
                    </Link>
                  </EmptyStateView>
                </div>
              );
            })()}
          {game && (
            <>
              {showInsufficientBalancePopup ? (
                <CasinoNewProvider group={GROUPS.CASINO} skipDataLoad>
                  <InsufficientBalance
                    game={game}
                    onGoBack={onGoBack}
                    setPaymentUrl={setPaymentUrl}
                  />
                </CasinoNewProvider>
              ) : (
                <div className={className}>
                  <Link className="back-to-container" to={fromPath || casinoPath}>
                    <Icon className="icon-back" name="back" />
                    <div className="casino-game-name">
                      {isDemoEnabled && (
                        <>
                          <Label message="demo" />
                          &nbsp;&ndash;&nbsp;
                        </>
                      )}
                      {game.name}
                    </div>
                  </Link>
                  <div className="casino-game-screen">
                    <iframe ref={iframeRef} src={src} title="Casino Game" />
                  </div>
                  {isDemoEnabled && (
                    <div className="casino-game-demo">
                      <Label className="title-small-text" message="demo_mode_title" />
                      <Button label="game_real_play_button" onClick={handlePlayGame} size="small" />
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        {showQuickDepositPopup && (
          <QuickDepositPopup
            afterDelay={null}
            onClose={closeQuickDepositPopup}
            onCloseClick={closeQuickDepositPopup}
            setPaymentUrl={setPaymentUrl}
          />
        )}
        {paymentUrl && <PaymentIframe paymentUrl={paymentUrl} setPaymentUrl={setPaymentUrl} />}
      </ModalPortal>
    </div>
  );
};

export default CasinoGameDetails;
